import {
  Badge,
  Box,
  Container,
  Flex,
  IconButton,
  SimpleGrid,
  Spacer,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useQuery } from "react-query";
import { Navigate, useParams } from "react-router-dom";
import { usePremium } from "../../../contexts/Premium";
import { GrUndo } from "react-icons/gr";

import { getSingleReadingOrder } from "../../../services/readingOrdersApi";
import Spinner from "../../../shared/components/Spinner";
import ReadingOrderHeader from "../../reading/components/ReadingOrderHeader";
import SuggestionForm from "../components/SuggestionForm";
import { useCallback, useMemo } from "react";
import PaginatedList, {
  PaginationControls,
} from "../../../shared/components/PaginatedList";
import useComicsPaginatedList from "../hooks/useComicsPaginatedList";
import useCorrections from "../hooks/useCorrections";

const ReadingOrderCorrection = () => {
  const { readingOrderId } = useParams();
  const { isPremium } = usePremium();
  const readingOrderQuery = useQuery(["readingOrder", readingOrderId], () => {
    return getSingleReadingOrder(readingOrderId);
  });

  const { corrections, mergeCorrections, correctionsLoaded, undoCorrection } =
    useCorrections(readingOrderId);

  const {
    comics,
    isLoadingComics,
    totalItems,
    currentPage,
    totalPages,
    handleJumpTo,
    onClickFirst,
    onClickLast,
  } = useComicsPaginatedList(readingOrderId, correctionsLoaded);

  const comicsData = useMemo(
    () => mergeCorrections(comics, corrections),
    [comics, corrections, mergeCorrections]
  );

  const renderComicRow = useCallback(
    ({ index, style }) => {
      const comic = comicsData[index];
      // console.log(comicsData);
      const actionColors = ["transparent", "green", "orange", "red"];
      const actionTexts = ["", "Added", "Edited", "Deleted"];
      return (
        <Flex
          justifyContent="space-between"
          alignItems="center"
          py="3"
          borderBottomWidth="1px"
          borderBottomColor="gray.900"
          borderLeftWidth={"5px"}
          borderLeftColor={
            !!comic.action
              ? actionColors[parseInt(comic.action)]
              : "transparent"
          }
          key={index}
          style={style}
        >
          <Box px="3">{comic?.action !== 1 && `${comic.order}.`}</Box>
          <Box flexGrow={2}>
            <Text
              fontSize="0.9em"
              lineHeight="1.25em"
              overflowWrap="break-word"
              wordBreak="break-word"
              cursor="pointer"
              onClick={() => {
                const url = `${window.location.origin}/comics/${comic.id}/view`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              {comic.title} {comic.story && `[${comic.story}]`}
            </Text>
          </Box>
          {comic.action && (
            <Box px="3">
              <Badge mr="3" colorScheme={actionColors[comic.action]}>
                {actionTexts[comic.action]}
              </Badge>
              <Tooltip hasArrow label="Remove my suggestion">
                <IconButton
                  onClick={() => undoCorrection(comic.correctionId)}
                  icon={<GrUndo color="white" />}
                  size="xs"
                />
              </Tooltip>
            </Box>
          )}
        </Flex>
      );
    },
    [comicsData, undoCorrection]
  );

  if (readingOrderQuery.isLoading) {
    return <Spinner />;
  }

  const readingOrder = readingOrderQuery.data.data;

  if (!isPremium && readingOrder && readingOrder.premiumOnly) {
    return <Navigate to={"/browse"} />;
  }

  return (
    <Container maxW="container.lg">
      <SimpleGrid mt="6" columns={{ sm: 1, md: 1, lg: 2 }} spacing={2}>
        <Box>
          <Box bg="gray.700" borderRadius="lg">
            <ReadingOrderHeader
              totalComics={readingOrder.comicsCount}
              readingOrder={readingOrder}
              coverHeight={170}
              hideProgress
            />
            <Box pt="3" px="6" pb="6">
              <Text fontSize="sm" color="gray.400" mt="2" ml="1">
                SUGGEST A CORRECTION
              </Text>
              <SuggestionForm />
            </Box>
          </Box>
        </Box>
        <Box bg="gray.700" borderRadius="lg">
          <Flex
            py="3"
            px="3"
            borderBottomColor="gray.900"
            borderBottomWidth="1px"
          >
            <Text fontSize="sm" color="gray.400" mt="2" ml="1">
              READING ORDER
            </Text>
            <Spacer />
          </Flex>
          {/* Reading order list */}
          {isLoadingComics ? (
            <Spinner />
          ) : (
            <PaginatedList
              totalItems={totalItems}
              renderRow={renderComicRow}
              items={comicsData}
            />
          )}
          {totalItems >= 500 && (
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              handleJumpTo={handleJumpTo}
              onClickFirst={onClickFirst}
              onClickLast={onClickLast}
            />
          )}
        </Box>
      </SimpleGrid>
    </Container>
  );
};

export default ReadingOrderCorrection;
